<template>
  <!----------------------------------------------------------------->
  <!-- LIST VIEW -->
  <!----------------------------------------------------------------->
  <template v-if="viewType === DealsListViewType.LIST">
    <curated-deals-table
      v-show="hasAccessToCuratedPackages"
      :all-curators="curators"
      :curated-packages="curatedPackages"
      :curated-packages-total-count="totalCount"
      :curated-packages-sort-by="sortBy"
      :is-curated-packages-sort-ascending="sortDirection.value === 'asc'"
      :is-curated-packages-loading="isLoading"
      :columns="columns"
      :page-size="pageSize"
      :previous-table-data="table"
      :selection-enabled="selectionEnabled"
      :lock-selection="lockSelection"
      :active-deal="activeDeal"
      @requested-deal-details="emit('requestedDealDetails', $event)"
      @filters-changed="onCuratedPackagesTableFiltersChanged"
      @page-size-changed="onPageSizeChanged"
      @loaded="onTableLoaded"
      @selection-changed="emit('selectionChanged', $event)" />
  </template>

  <!----------------------------------------------------------------->
  <!-- TILES VIEW -->
  <!----------------------------------------------------------------->
  <template v-else-if="viewType === DealsListViewType.TILES">
    <curated-deals-tiles-table
      v-show="hasAccessToCuratedPackages"
      :curators="curators"
      :curated-packages="curatedPackages"
      :total-count="totalCount"
      :sort-by="sortBy"
      :is-sort-ascending="isSortAscending"
      :is-loading="isLoading"
      :columns="columns"
      :page-size="pageSize"
      :demographics="demographics"
      :media-owners="mediaOwners"
      :buyer-organization-d-s-ps="buyerOrganizationDSPs"
      :sort-direction="sortDirection"
      :sort-direction-options="sortDirectionOptions"
      :sort-by-options="curatedPackagesSortByOptions"
      :previous-table-data="table"
      :is-selection-enabled="selectionEnabled"
      :selected-packages="selectedPackages"
      :lock-seleciton="lockSelection"
      @page-size-changed="onPageSizeChanged"
      @filters-changed="onCuratedPackagesTableFiltersChanged"
      @sort-direction-changed="onSortDirectionChanged"
      @sort-by-changed="onSortByChanged"
      @loaded="onTableLoaded"
      @selection-changed="emit('selectionChanged', $event)" />
  </template>
</template>

<script setup lang="ts">
  //#region Imports

  import { CanceledError } from 'axios';
  import { DateTime } from 'luxon';
  import { capitalize, computed, onBeforeMount, onMounted, Ref, ref } from 'vue';

  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import {
    Filter,
    FilterCollection,
    FilterComparisonOperator,
    Package,
    PackageStatus,
    QueryParams,
  } from '@/common/models';
  import Demographic from '@/common/models/demographics/Demographic';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import OrganizationDSP from '@/common/models/misc-api-models/OrganizationDSP';
  import ErrorHandler from '@/ErrorHandler';
  import { toServerDateTimeNoTimezone } from '@/globals';
  import CuratedDealsTable from '@/org-app/components/packages/CuratedDealsTable.vue';
  import CuratedDealsTilesTable from '@/org-app/components/packages/CuratedDealsTilesTable.vue';
  import DealsListViewType from '@/org-app/components/packages/DealsListViewType.enum';
  import { ISortBy, ISortDirection } from '@/org-app/components/packages/DealSorting';
  import { mapOrgAppActions } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  //#endregion

  //#region Props

  interface ICuratedDealsListProps {
    mediaOwners: MediaOwner[];
    curators: CuratorOrganization[];
    demographics: Demographic[];
    buyerOrganizationDSPs: OrganizationDSP[];
    viewType: DealsListViewType;
    sortDirectionOptions: ISortDirection[];
    selectionEnabled: boolean;
    lockSelection?: boolean;
    selectedPackages?: Package[];
    activeDeal?: Package;
  }

  const props = withDefaults(defineProps<ICuratedDealsListProps>(), {
    lockSelection: false,
    selectedPackages: () => [],
    activeDeal: null,
  });

  interface ICuratedDealsListEmits {
    (e: 'loaded', hasCuratedDeals: boolean): void;
    (e: 'selectionChanged', selectedPackages: Package[]): void;
    (e: 'requestedDealDetails', deal: Package): void;
  }

  const emit = defineEmits<ICuratedDealsListEmits>();

  //#endregion

  //#region Data

  const sortBy: Ref<ISortBy> = ref(null);
  const sortDirection: Ref<ISortDirection> = ref(null);
  const curatedPackages: Ref<Package[]> = ref([]);
  const filterText: Ref<string> = ref(null);
  const pageSize: Ref<number> = ref(10);
  const totalCount: Ref<number> = ref(0);
  const isLoading: Ref<boolean> = ref(false);
  const hasAccessToCuratedPackages = ref(false);

  // DOM refs
  const table = ref(null);

  const queryParamsExpand: string[] = [
    'package_properties',
    'package_external_exchange_restrictions',
    'inventory_targeting.co2e_emissions.score',
    'package_children.inventory_targeting.co2e_emissions.score',
    'package_fee',
    'estimated_delivery_statistics',
  ];

  //#endregion

  //#region Mapped actions, state, getters

  const { getCuratedPackages } = mapOrgAppActions();

  //#endregion

  //#region Computed

  const columns = computed<Record<string, TableColumn>>(() => ({
    link: Package.entityProperties.name.getTableColumn(),
    dealID: Package.entityProperties.dealIDFormatted.getTableColumn(),
    curator: TableColumn.fromObject({
      key: 'curatorOrganizationID',
      label: capitalize(i18n.global.t('names.curator')),
      property: 'curatorOrganizationID',
      allowSort: false,
      type: TableColumnTypes.list,
      source: () => props.curators,
      sourceID: 'curatorOrganizationID',
      sourceFilterProperty: 'organization_id',
    }),
    purchaseType: Package.entityProperties.purchaseType.getTableColumn({ allowSort: false }),
    co2EmissionsScore: TableColumn.fromObject({
      key: 'co2EmissionsScore',
      label: capitalize(i18n.global.t('names.co2EmissionsScore')).toUpperCase().replace('CO₂E', 'CO₂e'),
      property: 'co2EmissionsScore',
      allowSort: false,
      useAsFilter: false,
      shouldBeUppercased: false,
    }),
    dailyImpressionsFormatted: TableColumn.fromObject({
      key: 'dailyImpressions',
      label: capitalize(i18n.global.t('messages.dailyImpressions')),
      property: 'dailyImpressionsFormatted',
      allowSort: false,
      useAsFilter: false,
    }),
    eCPM: TableColumn.fromObject({
      key: 'eCPM',
      label: capitalize(i18n.global.t('names.eCPM')),
      property: 'cpmDescription',
      allowSort: false,
      useAsFilter: false,
    }),
    siteCountFormatted: TableColumn.fromObject({
      key: 'siteCount',
      label: capitalize(i18n.global.t('messages.siteCount')),
      property: 'siteCountFormatted',
      allowSort: false,
      useAsFilter: false,
    }),
    unitCountFormatted: TableColumn.fromObject({
      key: 'unitCount',
      label: capitalize(i18n.global.t('messages.screenCount')),
      property: 'unitCountFormatted',
      allowSort: false,
      useAsFilter: false,
    }),
    dealVisibility: Package.entityProperties.dealVisibility.getTableColumn({
      key: 'dealVisibility',
      property: 'buyer_access',
      sourceFilterProperty: 'buyer_access',
      shouldRenderColumn: false,
    }),
    modifiedOn: Package.entityProperties.modifiedOnFormatted.getTableColumn({
      allowSort: false,
    }),
  }));

  const curatedPackagesSortByOptions = computed(() => {
    const options: ISortBy[] = [
      {
        label: capitalize(i18n.global.t('names.name', { n: 1 })),
        property: 'name',
      },
      {
        label: capitalize(i18n.global.t('names.dealID', { n: 1 })),
        property: 'deal_id',
      },
    ];
    return options;
  });

  const isSortAscending = computed(() => sortDirection.value?.value === 'asc');

  //#endregion

  //#region Functions

  async function loadCuratedPackages(queryParams: QueryParams = null) {
    try {
      if (table.value === undefined) {
        setTimeout(loadCuratedPackages, 100);
        return;
      }
      isLoading.value = true;
      if (queryParams == null) queryParams = table.value.queryParams;
      queryParams.showLoader = false;
      // Ensure that the curated packages are currently available.
      const activeFilters = [
        new Filter(
          'flight_to',
          [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
          FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
        ),
        new Filter('state', [PackageStatus.Published], FilterComparisonOperator.EQUAL),
      ];
      if (!queryParams.filterCollection) {
        queryParams.filterCollection = new FilterCollection(activeFilters);
      } else {
        queryParams.filterCollection.filters.push(
          ...activeFilters.filter((x) => !queryParams.filterCollection.filters.some((y) => y.property === x.property)),
        );
      }
      queryParams.expand = queryParamsExpand;
      queryParams.showLoader = false;
      const response = await queryParams.run(getCuratedPackages);
      if (response) {
        curatedPackages.value = response.items;
        totalCount.value = response.totalCount;
      }
      isLoading.value = false;
    } catch (e) {
      if (!(e instanceof CanceledError)) isLoading.value = false;
      ErrorHandler.handleExceptionInUIComponent(e);
    }
  }

  function reloadCuratedPackages(queryParams: QueryParams) {
    queryParams.showLoader = false;
    loadCuratedPackages(queryParams);
  }

  function onCuratedPackagesTableFiltersChanged(queryParams?: QueryParams) {
    const sortByColumnNames = queryParams.sort != null ? queryParams.sort.replace(/ desc/gm, '') : '';
    if (
      queryParams.filterCollection?.filters.some(
        (x) => x.type === 'FilterGroup' && x.filters.some((y) => y.property === 'name'),
      )
    ) {
      [filterText.value] = queryParams.filterCollection.filters.find(
        (x) => x.type === 'FilterGroup' && x.filters.some((y) => y.property === 'name'),
      ).filters[0].values;
    }

    reloadCuratedPackages(queryParams);

    if (queryParams.sort != null && queryParams.sort !== '') {
      const sortByFromQueryParams = curatedPackagesSortByOptions.value.find(
        (x) =>
          x.property === queryParams.sort.split(' ')[0] ||
          (x.orderBy != null &&
            (sortByColumnNames.includes(x.orderBy.join(',')) ||
              sortByColumnNames.includes(x.orderBy.reverse().join(',')))),
      );
      if (sortByFromQueryParams.property !== sortBy.value.property) {
        sortBy.value = sortByFromQueryParams;
      }
      const sortDirectionFromQueryParams = props.sortDirectionOptions.find(
        (x) => x.value === (queryParams.sort.includes('desc') ? 'desc' : 'asc'),
      );
      if (sortDirection.value.value !== sortDirectionFromQueryParams.value) {
        sortDirection.value = sortDirectionFromQueryParams;
      }
    } else {
      sortBy.value = curatedPackagesSortByOptions.value.find((x) => x.property === 'name');
      sortDirection.value = props.sortDirectionOptions?.find((x) => x.value === 'asc');
    }
  }

  function onTableLoaded(childTable: typeof Table) {
    const previousTableData = table.value;
    table.value = childTable;
    if (previousTableData) {
      table.value.pageIndex = previousTableData.pageIndex;
      table.value.pageSize = previousTableData.pageSize;
      table.value.filterText = previousTableData.filterText;
      table.value.activeFilters = previousTableData.activeFilters;
      table.value.fireFiltersChanged();
    } else {
      onCuratedPackagesTableFiltersChanged(table.value.queryParams);
    }
    if (!props.lockSelection && props.selectedPackages.length > 0) {
      table.value.selectedItems = props.selectedPackages;
    }
  }

  function onSortByChanged(val: ISortBy) {
    sortBy.value = val;
  }

  function onSortDirectionChanged(val: ISortDirection) {
    sortDirection.value = val;
  }

  function onPageSizeChanged(val: number) {
    pageSize.value = val;
  }

  //#endregion

  //#region Lifecycle hooks

  onBeforeMount(() => {
    sortBy.value = curatedPackagesSortByOptions.value.find((x) => x.property === 'name');
    sortDirection.value = props.sortDirectionOptions?.find((x) => x.value === 'asc');
  });

  onMounted(async () => {
    try {
      //
      // Perform a get with a page size of 1 to figure out if the user has access to curated deals.
      //
      const qp = QueryParams.for({
        top: 1,
        sort: 'name',
      });
      qp.showLoader = false;
      const firstCuratedPackageResult = await qp.run(getCuratedPackages);
      hasAccessToCuratedPackages.value = firstCuratedPackageResult.items.length > 0;

      emit('loaded', hasAccessToCuratedPackages.value);
    } catch (e) {
      /* empty */
    }
  });

  defineExpose({
    table,
  });

  //#endregion
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;
</style>
